import React, { useEffect, useRef, useMemo } from "react";
import { Box, Card, Typography } from "@mui/material";
import * as echarts from "echarts";
import { CHART_TOOLTIP_STYLE } from "../const";

const ClientsChart = ({ clientStatus = [] }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom || clientStatus.length === 0) return;

    const chart = echarts.init(chartDom);
    const days = clientStatus.map((_, idx) => `D${idx + 1}`);
    const onlineData = clientStatus.map((item) => item?.online ?? 0);
    const newData = clientStatus.map((item) => item?.new ?? 0);

    const option = {
      grid: {
        top: 25,
        bottom: 30,
        left: 30,
        right: 15,
        containLabel: true,
      },
      legend: {
        bottom: 0,
        left: 30,
        icon: "rect",
        itemWidth: 10,
        itemHeight: 2,
        textStyle: {
          color: "#707ca8",
          fontSize: 11,
        },
        formatter: (name) => {
          if (name === "Online") {
            const lastVal = onlineData[onlineData.length - 1] ?? 0;
            return `${name}: ${lastVal}`;
          }
          if (name === "New") {
            const lastVal = newData[newData.length - 1] ?? 0;
            return `${name}: ${lastVal}`;
          }
          return name;
        },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "#fff",
        borderColor: "#e0e4f0",
        borderWidth: 1,
        textStyle: {
          color: "#333",
          fontSize: 12,
        },
        padding: [8, 12],
        extraCssText: CHART_TOOLTIP_STYLE,
        formatter: (params) => {
          const dayName = params[0].axisValue;
          let onlineVal = 0;
          let newVal = 0;
          params.forEach((p) => {
            if (p.seriesName === "Online") onlineVal = p.value;
            if (p.seriesName === "New") newVal = p.value;
          });
          return `
            <div style="font-weight: bold; margin-bottom: 4px;">${dayName}</div>
            <div style="display: flex; align-items: center; gap: 8px; justify-content: space-between;">
              <span style="display: flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 8px; height: 2px; background-color: #172664;"></span>
                Online
              </span>
              <span style="font-weight: bold;">${onlineVal}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; justify-content: space-between; margin-top: 2px;">
              <span style="display: flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 8px; height: 2px; background-color: #87A2E2;"></span>
                New
              </span>
              <span style="font-weight: bold;">${newVal}</span>
            </div>
          `;
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: days,
        axisLine: {
          lineStyle: {
            color: "#e0e4f0",
          },
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
        splitLine: {
          lineStyle: {
            color: "#f0f2f8",
          },
        },
      },
      series: [
        {
          name: "Online",
          type: "line",
          smooth: true,
          showSymbol: false,
          itemStyle: {
            color: "#172664",
          },
          lineStyle: {
            width: 2,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(23, 38, 100, 0.15)" },
              { offset: 1, color: "rgba(23, 38, 100, 0)" }
            ])
          },
          data: onlineData,
        },
        {
          name: "New",
          type: "line",
          smooth: true,
          showSymbol: false,
          itemStyle: {
            color: "#87A2E2",
          },
          lineStyle: {
            width: 2,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(135, 162, 226, 0.15)" },
              { offset: 1, color: "rgba(135, 162, 226, 0)" }
            ])
          },
          data: newData,
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [clientStatus]);

  if (clientStatus.length === 0) {
    return (
      <Card
        sx={{
          flex: 1,
          minWidth: "300px",
          p: 2.5,
          borderRadius: "8px",
          border: "1px solid #e0e4f0",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          minHeight: "260px",
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#172264", fontWeight: 600, mb: 2 }}>
          Site Network Status - Clients
        </Typography>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "#707ca8", fontSize: "14px" }}>No Data</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        flex: 1,
        minWidth: "300px",
        p: 2.5,
        borderRadius: "8px",
        border: "1px solid #e0e4f0",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: "#172264",
          fontWeight: 600,
          mb: 2,
        }}
      >
        Site Network Status - Clients
      </Typography>
      <Box sx={{ width: "100%", height: "180px" }} ref={chartRef} />
    </Card>
  );
};

const TrafficChart = ({ wirelessTrafficUsage = [] }) => {
  const chartRef = useRef(null);

  const scaleFactor = 1024 * 1024 * 1024;
  const unit = "GB";

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom || wirelessTrafficUsage.length === 0) return;

    const chart = echarts.init(chartDom);
    const days = wirelessTrafficUsage.map((_, idx) => `D${idx + 1}`);
    const uplinkData = wirelessTrafficUsage.map((item) => +((item?.uplink || 0) / scaleFactor).toFixed(2));
    const downlinkData = wirelessTrafficUsage.map((item) => +((item?.downlink || 0) / scaleFactor).toFixed(2));

    const option = {
      grid: {
        top: 20,
        bottom: 30,
        left: 30,
        right: 15,
        containLabel: true,
      },
      legend: {
        bottom: 0,
        left: 30,
        icon: "rect",
        itemWidth: 10,
        itemHeight: 2,
        textStyle: {
          color: "#707ca8",
          fontSize: 11,
        },
        formatter: (name) => {
          if (name === "Uplink") {
            const lastVal = uplinkData[uplinkData.length - 1] ?? 0;
            return `${name}: ${lastVal} ${unit}`;
          }
          if (name === "Downlink") {
            const lastVal = downlinkData[downlinkData.length - 1] ?? 0;
            return `${name}: ${lastVal} ${unit}`;
          }
          return name;
        },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: "#fff",
        borderColor: "#e0e4f0",
        borderWidth: 1,
        textStyle: {
          color: "#333",
          fontSize: 12,
        },
        padding: [8, 12],
        extraCssText: CHART_TOOLTIP_STYLE,
        formatter: (params) => {
          const dayName = params[0].axisValue;
          let uplinkVal = 0;
          let downlinkVal = 0;
          params.forEach((p) => {
            if (p.seriesName === "Uplink") uplinkVal = p.value;
            if (p.seriesName === "Downlink") downlinkVal = p.value;
          });
          return `
            <div style="font-weight: bold; margin-bottom: 4px;">${dayName}</div>
            <div style="display: flex; align-items: center; gap: 8px; justify-content: space-between;">
              <span style="display: flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 8px; height: 2px; background-color: #172664;"></span>
                Uplink
              </span>
              <span style="font-weight: bold;">${uplinkVal} ${unit}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px; justify-content: space-between; margin-top: 2px;">
              <span style="display: flex; align-items: center; gap: 6px;">
                <span style="display: inline-block; width: 8px; height: 2px; background-color: #87A2E2;"></span>
                Downlink
              </span>
              <span style="font-weight: bold;">${downlinkVal} ${unit}</span>
            </div>
          `;
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: days,
        axisLine: {
          lineStyle: {
            color: "#e0e4f0",
          },
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
      },
      yAxis: {
        type: "value",
        /* name: `Traffic (${unit})`,
        nameTextStyle: {
          color: "#707ca8",
          fontSize: 10,
          padding: [0, 0, 0, 20],
        }, */
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: "#707ca8",
          fontSize: 11,
        },
        splitLine: {
          lineStyle: {
            color: "#f0f2f8",
          },
        },
      },
      series: [
        {
          name: "Uplink",
          type: "line",
          smooth: true,
          showSymbol: false,
          itemStyle: {
            color: "#172664",
          },
          lineStyle: {
            width: 2,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(23, 38, 100, 0.15)" },
              { offset: 1, color: "rgba(23, 38, 100, 0)" }
            ])
          },
          data: uplinkData,
        },
        {
          name: "Downlink",
          type: "line",
          smooth: true,
          showSymbol: false,
          itemStyle: {
            color: "#87A2E2",
          },
          lineStyle: {
            width: 2,
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "rgba(135, 162, 226, 0.15)" },
              { offset: 1, color: "rgba(135, 162, 226, 0)" }
            ])
          },
          data: downlinkData,
        },
      ],
    };

    chart.setOption(option);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, [wirelessTrafficUsage, scaleFactor, unit]);

  if (wirelessTrafficUsage.length === 0) {
    return (
      <Card
        sx={{
          flex: 1,
          minWidth: "300px",
          p: 2.5,
          borderRadius: "8px",
          border: "1px solid #e0e4f0",
          boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          minHeight: "260px",
        }}
      >
        <Typography sx={{ fontSize: "14px", color: "#172264", fontWeight: 600, mb: 2 }}>
          Site Network Status - Wireless Traffic Usage
        </Typography>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: "#707ca8", fontSize: "14px" }}>No Data</Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        flex: 1,
        minWidth: "300px",
        p: 2.5,
        borderRadius: "8px",
        border: "1px solid #e0e4f0",
        boxShadow: "0px 2px 8px rgba(0,0,0,0.04)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          color: "#172264",
          fontWeight: 600,
          mb: 2,
        }}
      >
        Site Network Status - Wireless Traffic Usage
      </Typography>
      <Box sx={{ width: "100%", height: "180px" }} ref={chartRef} />
    </Card>
  );
};

const ClientTrafficChart = ({ chartsData = {} }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 3,
        width: "100%",
      }}
    >
      <ClientsChart clientStatus={chartsData.clientStatus || []} />
      <TrafficChart wirelessTrafficUsage={chartsData.wirelessTrafficUsage || []} />
    </Box>
  );
};

export default ClientTrafficChart;
